import React, { useState, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../stores/useEditorStore';
import {
  validateSTLFiles,
  validatePLYFiles,
  validateOBJFiles,
  createBlobUrl,
  createOBJBlobUrls,
} from '../utils/fileUploadUtils';

const FORMAT_OPTIONS = [
  { value: 'stl', label: 'STL Format' },
  { value: 'ply', label: 'PLY Format' },
  { value: 'obj', label: 'OBJ Format' },
];

const UNIT_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'mm', label: 'Millimeters' },
  { value: 'cm', label: 'Centimeters' },
  { value: 'm', label: 'Meters' },
  { value: 'inches', label: 'Inches' },
];

const ModelImporter = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState('stl');
  const [uploadError, setUploadError] = useState(null);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const fileInputRef = useRef(null);

  const { setUploadedModelData, modelScaleInfo, modelScaleUnit, setModelScaleUnit, modelManualScale, setModelManualScale } = useEditorStore(
    useShallow((state) => ({
      setUploadedModelData: state.setUploadedModelData,
      modelScaleInfo: state.modelScaleInfo,
      modelScaleUnit: state.modelScaleUnit,
      setModelScaleUnit: state.setModelScaleUnit,
      modelManualScale: state.modelManualScale,
      setModelManualScale: state.setModelManualScale,
    }))
  );

  const handleFormatChange = (e) => {
    setSelectedFormat(e.target.value);
    setUploadError(null);
    setSelectedFileNames([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadError(null);

    if (files.length === 0) return;

    try {
      let validationResult;

      if (selectedFormat === 'stl') {
        validationResult = validateSTLFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        // Create Blob URL for STL
        const blobUrl = createBlobUrl(validationResult.stlFile);
        setUploadedModelData({ type: 'stl', data: blobUrl });
        setSelectedFileNames([validationResult.stlFile.name]);
      } else if (selectedFormat === 'ply') {
        validationResult = validatePLYFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        // Create Blob URL for PLY
        const blobUrl = createBlobUrl(validationResult.plyFile);
        setUploadedModelData({ type: 'ply', data: blobUrl });
        setSelectedFileNames([validationResult.plyFile.name]);
      } else if (selectedFormat === 'obj') {
        validationResult = validateOBJFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        // Create Blob URLs for OBJ with MTL and textures
        const blobUrls = createOBJBlobUrls(validationResult);
        setUploadedModelData({ type: 'obj', data: blobUrls });
        // Display all selected file names
        const fileNames = [
          validationResult.objFile.name,
          ...(validationResult.mtlFile ? [validationResult.mtlFile.name] : []),
          ...validationResult.textureFiles.map(f => f.name)
        ];
        setSelectedFileNames(fileNames);
      }
    } catch (error) {
      setUploadError(`Error processing files: ${error.message}`);
    }

    // Reset file input (don't clear selected files display)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getAcceptAttribute = () => {
    if (selectedFormat === 'stl') return '.stl';
    if (selectedFormat === 'ply') return '.ply';
    if (selectedFormat === 'obj') return '.obj,.mtl,.jpg,.jpeg,.png';
    return '*';
  };

  const getMultipleAttribute = () => {
    return selectedFormat === 'obj';
  };

  const handleUnitChange = (e) => {
    setModelScaleUnit(e.target.value);
    // Model will be reloaded automatically when store changes trigger ThreeScene effect
  };

  const handleManualScaleChange = (e) => {
    const newScale = parseFloat(e.target.value);
    setModelManualScale(newScale);
  };

  const applyManualScale = () => {
    // Model will be reloaded automatically when store changes trigger ThreeScene effect
  };

  return (
    <div className="fixed top-4 right-4 z-50 bg-white/95 backdrop-blur-sm shadow-lg rounded-lg border-2 border-blue-500 max-w-sm max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 p-4 cursor-pointer sticky top-0 bg-white/95 backdrop-blur-sm border-b"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <h3 className="text-sm font-bold text-gray-800">Model Importer</h3>
        </div>
        <span className="text-gray-500 text-xs">{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Format Selection */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">File Format</h4>
            <select
              value={selectedFormat}
              onChange={handleFormatChange}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Import Model</h4>
            <div className="space-y-2">
              <div className="text-xs text-gray-600 mb-2">
                {selectedFormat === 'obj'
                  ? 'Select .obj file (optionally .mtl and textures)'
                  : `Select ${selectedFormat.toUpperCase()} file`}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple={getMultipleAttribute()}
                accept={getAcceptAttribute()}
                onChange={handleFileSelect}
                className="w-full text-xs file:px-3 file:py-1 file:text-xs file:border file:border-gray-300 file:rounded file:bg-blue-50 file:text-blue-600 file:cursor-pointer hover:file:bg-blue-100"
              />
              {selectedFileNames.length > 0 && (
                <div className="text-xs bg-green-50 p-2 rounded border border-green-200">
                  <div className="font-semibold text-green-700 mb-1">Selected Files:</div>
                  <div className="space-y-1">
                    {selectedFileNames.map((fileName, idx) => (
                      <div key={idx} className="text-green-600">• {fileName}</div>
                    ))}
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                  {uploadError}
                </div>
              )}
            </div>
          </div>

          {/* Scale Information */}
          {modelScaleInfo && (
            <div className="border-t pt-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Scale Information</h4>
              <div className="bg-blue-50 p-3 rounded space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Detected Unit:</span>
                  <span className="font-semibold text-blue-700 uppercase">{modelScaleInfo.detectedUnit}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Confidence:</span>
                  <span className={`font-semibold ${
                    modelScaleInfo.confidence === 'high' ? 'text-green-600' :
                    modelScaleInfo.confidence === 'medium' ? 'text-yellow-600' : 'text-orange-600'
                  }`}>
                    {modelScaleInfo.confidence}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Scale Factor:</span>
                  <span className="font-mono font-semibold">{modelScaleInfo.scaleFactor.toFixed(3)}x</span>
                </div>
              </div>

              {/* Dimensions */}
              <div className="mt-3 bg-green-50 p-3 rounded">
                <div className="text-xs font-semibold text-gray-700 mb-2">Dimensions (cm)</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-gray-600">Width</div>
                    <div className="font-mono font-semibold">{modelScaleInfo.dimensions.width.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Height</div>
                    <div className="font-mono font-semibold">{modelScaleInfo.dimensions.height.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Depth</div>
                    <div className="font-mono font-semibold">{modelScaleInfo.dimensions.depth.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Unit Override */}
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Unit Override</h4>
            <select
              value={modelScaleUnit}
              onChange={handleUnitChange}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Manual Scale Adjustment */}
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Manual Scale Adjustment</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0.1"
                  max="10"
                  step="0.1"
                  value={modelManualScale}
                  onChange={handleManualScaleChange}
                  className="flex-1"
                />
                <span className="font-mono text-xs font-semibold min-w-[3rem] text-right">
                  {modelManualScale.toFixed(1)}x
                </span>
              </div>
              <button
                onClick={applyManualScale}
                className="w-full px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
              >
                Apply Scale
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="border-t pt-4">
            <p className="text-xs text-gray-500 italic">
              Target: 1cm real-world = 1 unit in scene
            </p>
            <p className="text-xs text-green-600 mt-1 font-semibold">
              ✓ Changes apply instantly
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelImporter;
